import { constants, crypto as cv, type dto, type infra } from '@photo-vault/shared';
import { retryWithBackoff, runWithConcurrency } from '../upload/retry.js';
import { EncryptWorkerPool } from '../upload/workerPool.js';

// Mirrors the constants in uploadBulk.ts; we keep them duplicated rather than
// importing so each pipeline can be tuned independently.
const FILE_BATCH_SIZE = 50;
const PART_CONCURRENCY = 12;
const ENCRYPT_POOL_SIZE = 4;

export interface RotateProgress {
  total: number;
  rotated: number;
  current?: { photoId: string; phase: 'downloading' | 'reencrypting' | 'uploading' };
}

export interface RotateAlbumKeyInput {
  rootId: string;
  oldAlbumKey: CryptoKey;
  newAlbumKey: CryptoKey;
  token: string;
  metadata: infra.MetadataStore;
  storage: infra.StorageDriver;
  onProgress?: (snapshot: RotateProgress) => void;
  signal?: AbortSignal;
}

// Drives the photo-rotation phase only: assumes /rotation/init has already
// been called (so the album carries wrappedAlbumKeyNext) and the caller has
// the matching newAlbumKey in hand. Iterates /rotation/pending until empty
// then issues /rotation/commit.
export async function rotateAlbumPhotos(
  input: RotateAlbumKeyInput,
): Promise<{ commit: dto.RotateCommitResponse }> {
  const partSize = constants.MULTIPART_PART_SIZE_BYTES;
  const pool = new EncryptWorkerPool(ENCRYPT_POOL_SIZE);
  const { metadata, storage } = input;

  // Snapshot the initial pending count so progress is monotonic even though
  // the pending list shrinks each loop.
  const initialPending = await metadata.rotationPending(input.rootId, input.token);
  const total = initialPending.photos.length;
  let rotated = 0;
  const emit = (current?: RotateProgress['current']): void => {
    input.onProgress?.(current ? { total, rotated, current } : { total, rotated });
  };
  emit();

  try {
    while (true) {
      if (input.signal?.aborted) throw new DOMException('aborted', 'AbortError');
      const pending = await metadata.rotationPending(input.rootId, input.token);
      if (pending.photos.length === 0) break;

      // Process in chunks of FILE_BATCH_SIZE to bound a single round-trip.
      for (let chunkStart = 0; chunkStart < pending.photos.length; chunkStart += FILE_BATCH_SIZE) {
        if (input.signal?.aborted) throw new DOMException('aborted', 'AbortError');
        const chunk = pending.photos.slice(chunkStart, chunkStart + FILE_BATCH_SIZE);

        // 1. Presign downloads for the chunk.
        const dlResp = await metadata.downloadsBatch(
          {
            items: chunk.map((p) => ({ albumId: p.albumId, photoId: p.photoId })),
          },
          input.token,
        );
        const dlByPhotoId = new Map(dlResp.items.map((it) => [it.photoId, it]));

        // 2. For each photo: download → reencrypt in worker → produce bytes
        //    + new encryptedMeta. Bound concurrency to the worker pool so the
        //    main thread stays usable.
        interface Prepared {
          photoId: string;
          albumId: string;
          bytes: Uint8Array;
          encryptedMeta: string;
          encryptedRating?: string;
          partsCount: number;
        }
        const prepared = await runWithConcurrency<(typeof chunk)[number], Prepared>(
          chunk,
          ENCRYPT_POOL_SIZE,
          async (p) => {
            emit({ photoId: p.photoId, phase: 'downloading' });
            const dl = dlByPhotoId.get(p.photoId);
            if (!dl) throw new Error(`download URL missing for ${p.photoId}`);
            const res = await fetch(dl.url);
            if (!res.ok) throw new Error(`download HTTP ${res.status}`);
            const packed = await res.text();

            emit({ photoId: p.photoId, phase: 'reencrypting' });
            const reenc = await pool.reencrypt(
              packed,
              p.encryptedMeta,
              input.oldAlbumKey,
              input.newAlbumKey,
            );
            const partsCount = Math.max(1, Math.ceil(reenc.bytes.byteLength / partSize));
            if (partsCount > constants.MULTIPART_MAX_PARTS) {
              throw new Error(
                `${p.photoId}: ${partsCount} parti eccede il massimo di ${constants.MULTIPART_MAX_PARTS}`,
              );
            }
            // Re-encrypt the rating string under K_new (~150B WCV1 container,
            // cheap enough to do on the main thread). Skip if the photo wasn't
            // rated.
            let encryptedRatingNew: string | undefined;
            if (p.encryptedRating) {
              const plain = await cv.decryptText(p.encryptedRating, input.oldAlbumKey);
              encryptedRatingNew = await cv.encryptText(plain, input.newAlbumKey);
            }
            return {
              photoId: p.photoId,
              albumId: p.albumId,
              bytes: reenc.bytes,
              encryptedMeta: reenc.encryptedMeta,
              ...(encryptedRatingNew ? { encryptedRating: encryptedRatingNew } : {}),
              partsCount,
            };
          },
        );

        // 3. Presign new multipart uploads for the chunk.
        const batchResp = await metadata.rotatePhotosBatch(
          input.rootId,
          {
            items: prepared.map((p) => ({
              albumId: p.albumId,
              photoId: p.photoId,
              partsHint: p.partsCount,
              encryptedMeta: p.encryptedMeta,
              ...(p.encryptedRating ? { encryptedRating: p.encryptedRating } : {}),
              sizeBytes: p.bytes.byteLength,
            })),
          },
          input.token,
        );
        const respByPhotoId = new Map(batchResp.items.map((it) => [it.photoId, it]));

        // 4. Flat job list across the chunk so S3 stays saturated.
        interface PartJob {
          photoId: string;
          partNumber: number;
          url: string;
          chunk: Uint8Array;
        }
        const jobs: PartJob[] = [];
        const partsByPhotoId = new Map<string, { partNumber: number; etag: string }[]>();
        for (const p of prepared) {
          const r = respByPhotoId.get(p.photoId);
          if (!r) throw new Error(`backend missing presign for ${p.photoId}`);
          partsByPhotoId.set(p.photoId, []);
          for (let i = 0; i < r.partUrls.length; i++) {
            const start = i * partSize;
            const end = Math.min(start + partSize, p.bytes.byteLength);
            jobs.push({
              photoId: p.photoId,
              partNumber: i + 1,
              url: r.partUrls[i]!,
              chunk: p.bytes.subarray(start, end),
            });
          }
        }

        await runWithConcurrency(jobs, PART_CONCURRENCY, async (job) => {
          emit({ photoId: job.photoId, phase: 'uploading' });
          const { etag } = await retryWithBackoff(
            () => storage.uploadPart(job.url, job.chunk, input.signal),
            input.signal ? { signal: input.signal } : {},
          );
          partsByPhotoId.get(job.photoId)!.push({ partNumber: job.partNumber, etag });
        });

        // 5. Complete the multipart uploads for the chunk.
        await metadata.rotatePhotosComplete(
          input.rootId,
          {
            items: prepared.map((p) => {
              const r = respByPhotoId.get(p.photoId)!;
              const parts = partsByPhotoId
                .get(p.photoId)!
                .slice()
                .sort((a, b) => a.partNumber - b.partNumber);
              return {
                albumId: p.albumId,
                photoId: p.photoId,
                uploadId: r.uploadId,
                parts,
              };
            }),
          },
          input.token,
        );

        rotated += prepared.length;
        emit();
      }
    }
  } finally {
    pool.terminate();
  }

  // Re-encrypt every album name in the root subtree under K_album_new and
  // submit them at commit time. Album names are typically small strings, so
  // we do this on the main thread without the worker pool.
  const tree = await metadata.fetchAlbumTree(input.rootId, input.token);
  const albumNames: dto.RotateCommitRequest['albumNames'] = await Promise.all(
    tree.nodes.map(async (node) => {
      const plain = await cv.decryptText(node.encryptedName, input.oldAlbumKey);
      const encryptedName = await cv.encryptText(plain, input.newAlbumKey);
      return { albumId: node.id, encryptedName };
    }),
  );

  // Re-encrypt every tag name under K_album_new and stage them. /rotation/
  // commit will refuse if any TagDefinition is unstaged, so this MUST run
  // before rotateCommit. Empty roots (no tags) post an empty array, which
  // the backend accepts as a no-op.
  //
  // Retry path: if a previous commit failed after step 1.5 swapped some
  // tags, those tags are already on the new gen and unrecoverable with
  // K_old. The backend tolerates them being absent from input; we filter
  // them out here so the decrypt loop doesn't blow up.
  const { tags } = await metadata.listTags(input.rootId, input.token);
  const oldGen = (await metadata.rotationPending(input.rootId, input.token)).oldKeyGen;
  const stagedTags: dto.RotateTagsCommitRequest['tags'] = [];
  for (const tag of tags) {
    if ((tag.keyGen ?? 1) !== oldGen) continue;
    const plain = await cv.decryptText(tag.encryptedName, input.oldAlbumKey);
    const encryptedName = await cv.encryptText(plain, input.newAlbumKey);
    stagedTags.push({ tagId: tag.id, encryptedName });
  }
  await metadata.rotateTagsCommit(input.rootId, { tags: stagedTags }, input.token);

  const commit = await metadata.rotateCommit(input.rootId, { albumNames }, input.token);
  return { commit };
}
